import { Calendar, Check, MessageSquare, Tag, User, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAppData } from '../context/AppDataContext'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { hasFullVisibility } from '../hooks/useScopedWork'
import { TASK_STATUSES, type Priority, type Task, type TaskStatus } from '../data/types'
import { avatarColorFor, formatDateTime } from '../lib/format'
import { Avatar } from './Avatar'
import { Drawer } from './Drawer'
import { PriorityBadge } from './PriorityBadge'
import { useWorkspace } from '../context/WorkspaceContext'
import { StatusBadge } from './StatusBadge'
import { DatePicker } from './DatePicker'

const statuses: TaskStatus[] = TASK_STATUSES
const priorities: Priority[] = ['Low', 'Medium', 'High', 'Urgent']

export function TaskDetailDrawer({ task, onClose }: { task: Task | null; onClose: () => void }) {
  const { updateTask, loadTaskNotes, addTaskNote, clients, projects, directory } = useAppData()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const canReassign = hasFullVisibility(currentUser?.role)
  const client = task ? clients.find((c) => c.id === task.clientId) : undefined
  const project = task ? projects.find((p) => p.id === task.projectId) : undefined
  const assigneeOptions = canReassign ? directory : directory.filter((m) => m.id === task?.assigneeId)
  const [tagInput, setTagInput] = useState('')
  const { taskCategories: categories } = useWorkspace()
  const [noteInput, setNoteInput] = useState('')
  const [postingNote, setPostingNote] = useState(false)

  function addTag() {
    const value = tagInput.trim()
    if (!task || !value || task.tags.includes(value)) {
      setTagInput('')
      return
    }
    updateTask(task.id, { tags: [...task.tags, value] })
    setTagInput('')
  }

  function removeTag(tag: string) {
    if (!task) return
    updateTask(task.id, { tags: task.tags.filter((t) => t !== tag) })
  }

  // The thread is not part of the board read; fetch it when a task opens.
  const openId = task?.id
  useEffect(() => {
    if (openId === undefined) return
    loadTaskNotes(openId).catch(() => showToast('Could not load the notes for this task.'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId])

  async function submitNote() {
    const text = noteInput.trim()
    if (!task || !text || postingNote) return
    setPostingNote(true)
    try {
      await addTaskNote(task.id, text)
      setNoteInput('')
    } catch {
      showToast('Could not post note. Please try again.')
    } finally {
      setPostingNote(false)
    }
  }

  return (
    <Drawer open={!!task} onClose={onClose} title="Task details">
      {task && (
        <div className="flex flex-col gap-6 px-6 py-5">
          <div>
            <p className="text-xs font-medium text-shell-text">{client?.name} &middot; {project?.name}</p>
            <h3 className="mt-1 font-display text-lg font-semibold text-shell-text-active">{task.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-shell-text">{task.description}</p>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-shell-text">Status</p>
            <div className="flex flex-wrap gap-2">
              {statuses.map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    updateTask(task.id, { status: s })
                    showToast(`Task moved to ${s}`)
                  }}
                  className="transition-transform active:scale-95"
                >
                  <StatusBadge
                    status={s}
                    className={task.status === s ? 'ring-2 ring-offset-1 ring-brand-300 ring-offset-[var(--color-shell)]' : 'opacity-50'}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-shell-text">
              <Tag size={12} /> Priority
            </p>
            <div className="flex flex-wrap gap-2">
              {priorities.map((p) => (
                <button key={p} onClick={() => updateTask(task.id, { priority: p })} className="transition-transform active:scale-95">
                  <PriorityBadge
                    priority={p}
                    className={task.priority === p ? 'ring-2 ring-offset-1 ring-brand-300 ring-offset-[var(--color-shell)]' : 'opacity-50'}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-shell-text">
              <User size={12} /> Assigned to
            </p>
            <div className="grid grid-cols-2 gap-2">
              {assigneeOptions.map((m) => (
                <button
                  key={m.id}
                  disabled={!canReassign}
                  onClick={() => canReassign && updateTask(task.id, { assigneeId: m.id })}
                  className={`flex items-center gap-2 rounded-xl border px-2.5 py-2 text-left text-sm transition-colors ${
                    task.assigneeId === m.id
                      ? 'border-brand-400 bg-brand-500/15 text-brand-500'
                      : 'border-shell-border text-shell-text hover:bg-shell-border/40'
                  } ${!canReassign ? 'cursor-default' : ''}`}
                >
                  <Avatar name={m.name} color={m.color} size="xs" />
                  <span className="truncate">{m.name}</span>
                  {task.assigneeId === m.id && <Check size={13} className="ml-auto shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-shell-text">
              <Calendar size={12} /> Due date
            </p>
            <DatePicker
              value={task.dueDate}
              onChange={(e) => updateTask(task.id, { dueDate: e.target.value })}
              placeholder="No due date"
              aria-label="Due date"
              className="w-full rounded-xl border border-shell-border bg-shell px-3 py-2 text-sm text-shell-text-active"
            />
          </div>

          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-shell-text">
              <Tag size={12} /> Tags
            </p>
            <div className="flex flex-wrap gap-1.5">
              {task.tags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-1 rounded-full bg-shell-border/50 py-1 pl-2.5 pr-1.5 text-xs font-medium text-shell-text"
                >
                  {tag}
                  <button
                    onClick={() => removeTag(tag)}
                    aria-label={`Remove tag ${tag}`}
                    className="rounded-full p-0.5 text-shell-text/70 transition-colors hover:bg-shell-border hover:text-shell-text-active"
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addTag()
                  }
                }}
                placeholder="Add a tag..."
                className="w-full rounded-xl border border-shell-border bg-shell px-3 py-1.5 text-sm text-shell-text-active outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
              />
              <button
                onClick={addTag}
                disabled={!tagInput.trim()}
                className="shrink-0 rounded-xl border border-shell-border px-3 py-1.5 text-sm font-medium text-shell-text transition-colors hover:bg-shell-border/40 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Add
              </button>
            </div>
            {categories.some((c) => !task.tags.some((t) => t.toLowerCase() === c.toLowerCase())) && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {categories
                  .filter((c) => !task.tags.some((t) => t.toLowerCase() === c.toLowerCase()))
                  .map((c) => (
                    <button
                      key={c}
                      onClick={() => updateTask(task.id, { tags: [...task.tags, c] })}
                      className="rounded-full border border-dashed border-shell-border px-2.5 py-0.5 text-xs text-shell-text hover:border-brand-400 hover:text-brand-600"
                    >
                      + {c}
                    </button>
                  ))}
              </div>
            )}
          </div>

          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-shell-text">
              <MessageSquare size={12} /> Notes
            </p>
            <div className="flex flex-col gap-3">
              {task.notes.length === 0 && <p className="text-sm text-shell-text/60">No notes yet.</p>}
              {task.notes.map((note) => (
                <div key={note.id} className="flex items-start gap-2.5">
                  <Avatar name={note.authorName} color={avatarColorFor(note.authorId)} size="xs" className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-sm font-semibold text-shell-text-active">{note.authorName}</span>
                      <span className="text-[11px] text-shell-text">{formatDateTime(note.createdAt)}</span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap rounded-xl rounded-tl-sm bg-shell-border/40 px-3 py-2 text-sm leading-relaxed text-shell-text-active">
                      {note.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-start gap-2">
              <textarea
                value={noteInput}
                onChange={(e) => setNoteInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    submitNote()
                  }
                }}
                placeholder="Add a note..."
                rows={2}
                className="w-full resize-none rounded-xl border border-shell-border bg-shell px-3 py-2 text-sm text-shell-text-active outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
              />
              <button
                onClick={submitNote}
                disabled={!noteInput.trim() || postingNote}
                className="shrink-0 self-stretch rounded-xl bg-brand-500 px-3 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Post
              </button>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  )
}
